import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const PublicApply = () => {
  const { companyId, jobId } = useParams();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    skills: '',
    experience: '',
    noticePeriod: '',
  });

  const [jobDetails, setJobDetails] = useState(null);
  const [loadingJob, setLoadingJob] = useState(true);
  const [validationErrors, setValidationErrors] = useState({});
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchJobDetails();
  }, [jobId]);

  const fetchJobDetails = async () => {
    try {
      setLoadingJob(true);
      const response = await axios.get(`${API_URL}/api/jobpost/public/${jobId}`);
      if (response.data && response.data.job) {
        setJobDetails(response.data.job);
      } else {
        setSnackbar({
          open: true,
          message: 'Job details could not be found.',
          severity: 'error'
        });
      }
    } catch (error) {
      //console.error('Error fetching job details:', error);
      setSnackbar({
        open: true,
        message: 'Failed to retrieve job information.',
        severity: 'error'
      });
    } finally {
      setLoadingJob(false);
    }
  };

  const isValidPhoneNumber = (phone) => {
    return phone && /^[6-9]\d{9}$/.test(phone);
  };

  const isValidEmail = (email) => {
    return email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateFile = (file) => {
    if (!file) {
      return 'Resume is required';
    }

    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/rtf',
      'text/rtf',
      'text/plain',
      'application/vnd.oasis.opendocument.text'
    ];
    
    const allowedExtensions = ['.pdf', '.doc', '.docx', '.rtf', '.txt', '.odt'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    const isValidType = allowedTypes.includes(file.type) || allowedExtensions.includes(fileExtension);
    
    if (!isValidType) {
      return 'Please upload PDF, DOC, DOCX, RTF, TXT, or ODT files only';
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return 'File size must be less than 10MB';
    }

    return '';
  };

  const validateField = (name, value) => {
    let error = '';
    
    switch (name) {
      case 'phone':
        if (!isValidPhoneNumber(value)) {
          error = 'Please enter a valid 10-digit phone number starting with 6-9';
        }
        break;
      case 'email':
        if (!isValidEmail(value)) {
          error = 'Please enter a valid email address';
        }
        break;
      case 'name':
        if (!value || value.trim().length < 2) {
          error = 'Name must be at least 2 characters long';
        }
        break;
      case 'experience':
        if (value === '' || value < 0) {
          error = 'Please enter valid years of experience';
        }
        break;
      default:
        break;
    }
    
    return error;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    const error = validateField(name, value);
    setValidationErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    
    if (selectedFile) {
      const error = validateFile(selectedFile);
      setFileError(error);
      
      if (!error) {
        setFile(selectedFile);
      } else {
        setFile(null);
        e.target.value = '';
      }
    } else {
      setFile(null);
      setFileError('');
    }
  };

  const validateForm = () => {
    const errors = {};
    const requiredFields = ['name', 'email', 'phone', 'experience'];

    requiredFields.forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        errors[field] = error;
      }
    });

    const fileValidationError = validateFile(file);
    if (fileValidationError) {
      setFileError(fileValidationError);
      errors.file = fileValidationError;
    } else {
      setFileError('');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: "Please fix the validation errors before submitting.",
        severity: "error"
      });
      return;
    }
  
    setSubmitLoading(true);
  
    try {
      const formDataToSend = new FormData();
      
      formDataToSend.append('name', formData.name.trim());
      formDataToSend.append('email', formData.email.trim());
      formDataToSend.append('phone', formData.phone.trim());
      formDataToSend.append('experience', formData.experience.toString());
      formDataToSend.append('positionApplied', jobDetails.jobTitle);
      formDataToSend.append('department', jobDetails.department || '');
      formDataToSend.append('location', jobDetails.location || '');
      formDataToSend.append('description', jobDetails.description || '');
      formDataToSend.append('noticePeriod', formData.noticePeriod || '');
      formDataToSend.append('company_id', companyId);
      formDataToSend.append('applicationType', 'application');
      
      const skillsArray = formData.skills 
        ? formData.skills.split(',').map(skill => skill.trim()).filter(skill => skill !== '')
        : [];
      formDataToSend.append('skills', JSON.stringify(skillsArray));
      
      if (file) {
        formDataToSend.append('resume', file);
      }
  
      const endpoint = `${API_URL}/api/refer/apply-public`;
  
      await axios.post(endpoint, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 30000
      });
      
      setSuccess(true);
      setSnackbar({
        open: true,
        message: 'Application submitted successfully!',
        severity: 'success'
      });
      
    } catch (error) {
      //console.error('Error submitting application:', error);
      let errorMessage = 'Failed to submit your application. Please try again.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  if (loadingJob) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress size={50} />
      </Box>
    );
  }

  if (!jobDetails) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography variant="h5" color="error" gutterBottom fontWeight="bold">
            Job Posting Unavailable
          </Typography>
          <Typography variant="body1" color="text.secondary">
            This job vacancy link is invalid or may have expired. Please verify the link or check back later.
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper elevation={4} sx={{ p: 6, textAlign: 'center', borderRadius: 3, border: '1px solid #e2e8f0' }}>
          <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h4" gutterBottom fontWeight="bold" color="text.primary">
            Thank You!
          </Typography>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Your application for <strong>{jobDetails.jobTitle}</strong> has been received.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            A confirmation email will be sent shortly. Our recruitment team will review your profile and contact you within 5-7 business days if your skills match our requirements.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 5 }}>
      <Paper elevation={4} sx={{ p: 5, borderRadius: 3, border: '1px solid #e2e8f0' }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" color="primary">
          Join Our Team
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          Submit your details below to apply for the position.
        </Typography>

        <Box sx={{ mt: 3, p: 3, bgcolor: 'action.hover', borderRadius: 2, mb: 4 }}>
          <Typography variant="h6" fontWeight="bold" color="text.primary">
            {jobDetails.jobTitle}
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">
                <strong>Department:</strong> {jobDetails.department}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">
                <strong>Location:</strong> {jobDetails.location}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">
                <strong>Job Type:</strong> {jobDetails.employmentType}
              </Typography>
            </Grid>
          </Grid>
          {jobDetails.description && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Role Overview:
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                {jobDetails.description}
              </Typography>
            </Box>
          )}
        </Box>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Typography variant="h6" gutterBottom sx={{ borderBottom: '1px solid #eee', pb: 1, mb: 3 }} fontWeight="bold">
            Application Form
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                id="name"
                label="Full Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={!!validationErrors.name}
                helperText={validationErrors.name}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="email"
                required
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                error={!!validationErrors.email}
                helperText={validationErrors.email}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                id="phone"
                label="Phone Number"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                error={!!validationErrors.phone}
                helperText={validationErrors.phone}
                placeholder="Enter 10-digit mobile number"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="skills"
                name="skills"
                label="Key Skills"
                placeholder="e.g. React, Node.js, Python (comma separated)"
                value={formData.skills}
                onChange={handleChange}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                id="experience"
                label="Years of Experience"
                name="experience"
                type="number"
                value={formData.experience}
                onChange={handleChange}
                error={!!validationErrors.experience}
                helperText={validationErrors.experience}
                inputProps={{ min: 0 }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="notice-period-label">Notice Period</InputLabel>
                <Select
                  labelId="notice-period-label"
                  id="noticePeriod"
                  name="noticePeriod"
                  value={formData.noticePeriod}
                  label="Notice Period"
                  onChange={handleChange}
                >
                  <MenuItem value="">Not specified</MenuItem>
                  <MenuItem value="Immediate">Immediate</MenuItem>
                  <MenuItem value="15 days">15 days</MenuItem>
                  <MenuItem value="30 days">30 days</MenuItem>
                  <MenuItem value="60 days">60 days</MenuItem>
                  <MenuItem value="90 days">90 days</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{
                border: '2px dashed',
                borderColor: fileError ? 'error.main' : file ? 'success.main' : 'grey.400',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                bgcolor: 'background.paper',
                cursor: 'pointer',
                position: 'relative'
              }}>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.rtf,.txt,.odt"
                  onChange={handleFileChange}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: 'pointer'
                  }}
                  required
                />
                <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body1" fontWeight="bold">
                  {file ? file.name : 'Upload your resume'}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  PDF, DOC, DOCX, RTF, TXT or ODT (Max 10MB)
                </Typography>
                {fileError && (
                  <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                    {fileError}
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} sx={{ mt: 2 }}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={submitLoading}
                sx={{ py: 1.5, fontWeight: 'bold' }}
              >
                {submitLoading ? <CircularProgress size={26} /> : 'Submit Application'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PublicApply;
